import { DateTime } from 'luxon';
import { RRule } from 'rrule';

import { Timezone } from '@willow/types-iso';

export const getNextDate = (rrule: RRule, startDate: DateTime, tzid: Timezone = 'America/New_York') => {
  // RRule does NOT deal with timezone offsets, so everything is stored in UTC
  // So we need to take in dates here and ignore the offset
  const convertedDate = startDate.setZone('utc', { keepLocalTime: true }).toJSDate();
  const nextDate = rrule.after(convertedDate);
  if (!nextDate) return null;

  // Convert date into timezone specific time. Making sure that offset doesn't impact the time, so
  // for example if we expect a daily report at 9am we will always return 9am in the timezone specified
  return DateTime.fromJSDate(nextDate).toUTC().setZone(tzid, { keepLocalTime: true });
};

export const getReportRange = (rrule: RRule, deliveryDate: DateTime, tzid: Timezone = 'America/New_York') => {
  const reportRRule = rrule.clone();
  reportRRule.options.byhour = [0];
  reportRRule.options.byminute = [0];

  // RRule does NOT deal with timezone offsets, so everything is stored in UTC
  // So we need to take in dates here and ignore the offset
  const convertedDeliveryDate = deliveryDate.startOf('day').setZone('utc', { keepLocalTime: true }).toJSDate();

  const fromDate = reportRRule.before(convertedDeliveryDate);
  const toDate = reportRRule.before(convertedDeliveryDate, true);

  // Convert date into timezone specific time. Making sure that offset doesn't impact the time, so
  // for example if we expect a daily report at 9am we will always return 9am in the timezone specified
  return {
    fromDate: fromDate ? DateTime.fromJSDate(fromDate).toUTC().setZone(tzid, { keepLocalTime: true }) : null,
    toDate: toDate
      ? DateTime.fromJSDate(toDate).toUTC().setZone(tzid, { keepLocalTime: true }).minus({ seconds: 1 })
      : null,
  };
};

type ReportDates = {
  fromDate: DateTime | null;
  toDate: DateTime | null;
  deliveryDate: DateTime | null;
};
const EMPTY_REPORT: ReportDates = {
  fromDate: null,
  toDate: null,
  deliveryDate: null,
};

export const getNextCompanyReportDates = (
  rrule: RRule,
  startDate?: DateTime,
  tzid: Timezone = 'America/New_York',
  deliveryHour: number = 0,
): ReportDates => {
  startDate = startDate || DateTime.now().setZone(tzid).set({ hour: deliveryHour });

  const deliveryDate = getNextCompanyReportDatesDeliveryDate(rrule, startDate, tzid);

  // If we have a delivery date, return the new date and the range for it
  // Otherwise, return a null delivery date and range
  return deliveryDate
    ? {
        deliveryDate,
        ...getReportRange(rrule, deliveryDate, tzid),
      }
    : EMPTY_REPORT;
};

const getNextCompanyReportDatesDeliveryDate = (
  rrule: RRule,
  startDate: DateTime,
  tzid: Timezone = 'America/New_York',
) => {
  const deliveryDate = getNextDate(rrule, startDate, tzid);

  if (!deliveryDate) return null;

  // If the delivery date is in the past, try again, using the delivery date as the start date
  if (deliveryDate < DateTime.now()) {
    return getNextCompanyReportDatesDeliveryDate(rrule, deliveryDate, tzid);
  }

  return deliveryDate;
};
